import React, { HTMLAttributes, PropsWithChildren, useMemo, useState } from "react";
import { SubTabsDataType } from "../../layouts/header";
import CtaComponent from "../reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE } from "../../data/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { sendGTMEvent } from "../../helpers/ga-helpers";
import { getLocalStorage, setLocalStorage } from "../../utils";
import { feature_request } from "../../data/local-storage-constants";
import { captureInterest } from "../../helpers/feedback-helpers";

const data = {
  fallbackInfoRequested: "Your request has been captured successfully. The free licenses will be activated within 48 hours.",
  fallbackInfoText: "Oops!! You don’t seem to have access for this feature. Please click below to start your free 30 day trial period.",
  requestedCTA: "Licence Request Submitted",
  requestedInfo: "Your licence will get activated within 48 hours",
  requestCTA: "Request License",
};

type LicenceCheckLayoutProps = PropsWithChildren & {
  NoAccessComponent?: React.ComponentType;
  tabData: SubTabsDataType;
  customContainerClass?: string;
}

const LicenceCheckLayout: React.FC<LicenceCheckLayoutProps> = ({ children, tabData, NoAccessComponent, customContainerClass = "" }) => {
  const userRole = useSelector((state: RootState) => state.businessReducer.userData?.role);
  const licences = useSelector((state: RootState) => (state.businessReducer.licences || []).map((licence: { licenceCode: string }) => licence.licenceCode));
  const required_licences = tabData.conditionsCheck?.licence || [];
  const required_roles = tabData.conditionsCheck?.role || [];
  //check if this page needs license and if they have that license
  const hasFeatureAccess = useMemo(() => {
    return required_licences.findIndex(licence => licences.includes(licence)) !== -1;
  }, [licences]);
  const hasRoleAccess = useMemo(() => {
    return !required_roles.length || required_roles.includes(userRole);
  }, [userRole]);
  const event = tabData.conditionsCheck?.ga_event || "";
  const [isRequested, setRequested] = useState(!!getLocalStorage(feature_request(event)) || false); //get localStorage state

  //check for licence and the role
  if (!required_licences?.length || (hasFeatureAccess && hasRoleAccess))
    return <span className="w-full h-full">{children}</span>

  return <div className={`flex h-full w-full gap-8 items-center justify-center flex-col px-40 ${customContainerClass} `}>
    {NoAccessComponent && <NoAccessComponent />}
    {!NoAccessComponent && <div>{isRequested ? data.fallbackInfoRequested : data.fallbackInfoText}</div>}
    <RequestLicence isRequested={isRequested} setRequested={setRequested} event={event} />
    {isRequested && <div className="text-black/60 text-[14px]">{data.requestedInfo}</div>}
  </div>
}

const RequestLicence: React.FC<{ event: string; isRequested: boolean; setRequested: (val: boolean) => void; } & HTMLAttributes<HTMLDivElement>> = ({ event, isRequested, setRequested }) => {

  async function sendFeatureRequest() {
    if (isRequested) return;
    const data = { dimension6: new Date().toLocaleDateString("en-IN") };
    const status = await captureInterest({ ...data, dimension7: event });
    if (status) { //if api call is success; update state
      setRequested(true);
      setLocalStorage(feature_request(event), true);
    }
    sendGTMEvent({ eventName: event, data });
  }

  return <CtaComponent customClass='w-1/2' onCtaClick={sendFeatureRequest} ctaText={isRequested ? data.requestedCTA : data.requestCTA} ctaSize={CTA_SIZE.medium} isDisabled={isRequested} ctaType={CTA_TYPE.active_cta} />
}

export default LicenceCheckLayout;
